import { createContext, useContext, useState, useCallback } from "react";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("en");
  const [event, setEvent] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState(["en"]);

  // Function to update event data and language accordingly
  const updateEvent = useCallback(
    (eventData) => {
      // If event id or language has changed, update the event
      if (
        eventData &&
        (eventData.id !== event?.id || eventData.language !== event?.language)
      ) {
        setEvent(eventData);

        // Parse secondaryLanguages if it's a string (from DB)
        let parsedSecondaryLanguages = eventData.secondaryLanguages;
        if (typeof eventData.secondaryLanguages === "string") {
          try {
            parsedSecondaryLanguages = JSON.parse(eventData.secondaryLanguages);
          } catch (error) {
            console.error("Error parsing secondaryLanguages:", error);
            parsedSecondaryLanguages = [];
          }
        }

        // Build available languages array: main language + secondary languages
        const languages = [
          eventData.language,
          ...(parsedSecondaryLanguages || []),
        ].filter(Boolean);
        const newLanguages = languages.length > 0 ? languages : ["en"];

        setAvailableLanguages(newLanguages);

        // Set language to event's main language if it's different
        if (eventData.language && eventData.language !== language) {
          setLanguage(eventData.language);
        }
      }
    },
    [event?.id, language, event?.language]
  );

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        event,
        availableLanguages,
        updateEvent,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
